import React, { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';
import { LockClosedIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import { authApi, type TUsuario } from '../../api/client';
import { isPublicPath } from '../../utils/publicPaths';
import { descartarTelemetria } from '../../utils/telemetria';

/**
 * Dialog modal que aparece quando o token JWT expira durante uso da aplicacao.
 *
 * Antes esse cenario era tratado redirecionando direto pra /login — o usuario
 * perdia tudo que estava fazendo (filtros aplicados, edicoes em andamento,
 * pedidos sendo montados). Agora abre um dialog centralizado com campo de senha
 * preenchido com o ultimo login digitado; ao continuar, o request original
 * volta a funcionar e a tela atual continua exatamente onde estava.
 *
 * Triggers via custom event `auth:session-expired` disparado pelo axios
 * interceptor quando vem 401 de qualquer endpoint autenticado.
 */
const SessionExpiredDialog: React.FC = () => {
  const [aberto, setAberto] = useState(false);
  const [loginOuEmail, setLoginOuEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Fecha o dialog assim que a rota vira publica. O 401 que expira a sessao dispara
  // DUAS coisas em paralelo: este dialog (via evento) e o redirect pra /login (o
  // getProfile falha -> isAuthenticated=false -> <Navigate> no Layout/ProtectedRoute).
  // Sem isto, o dialog abre numa rota protegida e sobrevive ao redirect, aparecendo
  // por cima do proprio /login — que ja tem o form de login. O guard do handler so
  // roda no instante do evento; este roda a cada mudanca de rota.
  useEffect(() => {
    if (aberto && isPublicPath(location.pathname)) setAberto(false);
  }, [aberto, location.pathname]);

  useEffect(() => {
    const handler = () => {
      // Defensivo: ignora se ja esta em rota publica ou se o dialog ja esta aberto.
      if (isPublicPath(window.location.pathname)) return;
      if (aberto) return;

      // Pre-preenche o login com o ultimo usuado, fallback pro email do user atual.
      const ultimo = localStorage.getItem('last_login');
      const user = queryClient.getQueryData<TUsuario | null>(['user']);
      const sugerido = ultimo || user?.email || user?.login || '';
      setLoginOuEmail(sugerido);
      setSenha('');
      setErro(null);
      setAberto(true);
    };
    window.addEventListener('auth:session-expired', handler);
    return () => window.removeEventListener('auth:session-expired', handler);
  }, [aberto, queryClient]);

  // Nunca sobre uma rota publica (o /login ja tem seu proprio form).
  if (!aberto || isPublicPath(location.pathname)) return null;

  const handleContinuar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!loginOuEmail || !senha) {
      setErro('Preencha login e senha pra continuar.');
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const resp = await authApi.login({ loginOuEmail, senha, lembrarMe: true });
      const { token, usuario } = resp.data;
      // O campo de login é editável — pode ser outra conta. Descarta antes de
      // trocar o token pra não postar a fila do usuário anterior na conta nova.
      descartarTelemetria();
      localStorage.setItem('auth_token', token);
      localStorage.setItem('last_login', loginOuEmail);
      queryClient.setQueryData(['user'], usuario);
      // Refaz todas as queries — a tela atual recarrega seus dados sem perder o
      // estado local (filtros aplicados, dialogs abertos, edicoes na grid).
      queryClient.invalidateQueries();
      setAberto(false);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string }; status?: number } };
      setErro(ax.response?.data?.message || 'Não foi possível entrar. Verifique senha e tente de novo.');
    } finally {
      setEnviando(false);
    }
  };

  const handleSair = () => {
    // SPA logout (sem reload) — descarta a fila antes que o próximo login
    // nesta aba a envie com o token de outro usuário.
    descartarTelemetria();
    localStorage.removeItem('auth_token');
    queryClient.setQueryData(['user'], null);
    queryClient.clear();
    setAberto(false);
    navigate('/login', { replace: true });
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl dark:bg-zinc-900">
        <div className="flex items-center gap-3 border-b border-zinc-200 px-5 py-4 dark:border-zinc-800">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-100 text-amber-600 dark:bg-amber-950 dark:text-amber-400">
            <LockClosedIcon className="h-5 w-5" />
          </div>
          <div className="leading-tight">
            <h2 className="text-base font-semibold text-zinc-900 dark:text-zinc-100">
              Sua sessão expirou
            </h2>
            <p className="mt-0.5 text-[11px] text-zinc-500">
              Digite a senha pra continuar — você não vai perder o que está fazendo.
            </p>
          </div>
        </div>

        <form onSubmit={handleContinuar} className="space-y-3 px-5 py-4">
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
              Usuário
            </label>
            <input
              type="text"
              value={loginOuEmail}
              onChange={(e) => setLoginOuEmail(e.target.value)}
              autoComplete="username"
              autoFocus={!loginOuEmail}
              className="mt-1 w-full rounded border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
            />
          </div>
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">
              Senha
            </label>
            <input
              type="password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
              autoComplete="current-password"
              autoFocus={!!loginOuEmail}
              className="mt-1 w-full rounded border border-zinc-300 bg-white px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100"
            />
          </div>

          {erro && (
            <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
              {erro}
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleSair}
              disabled={enviando}
              className="inline-flex items-center gap-1 rounded-lg border border-zinc-300 bg-white px-3 py-2 text-xs text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
            >
              <ArrowRightOnRectangleIcon className="h-3.5 w-3.5" />
              Sair e voltar pro login
            </button>
            <button
              type="submit"
              disabled={enviando || !senha}
              className="rounded-lg bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {enviando ? 'Entrando...' : 'Continuar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SessionExpiredDialog;
